import { call, put, select, take } from 'redux-saga/effects';
import URLParse from 'url-parse';

import { auth as appClientAuth } from '@edf-pkg/app-client';
import { store as mainAppStore } from '@edf-pkg/app-main';

import { store as i18nStore } from '$app-web/i18n';
import {
    errorHandle,
    getWebApplicationURL,
    isLoadUserDataFromEnvEnabled,
    isLoadUserDataFromLocalStorageEnabled,
    isLoadUserDataFromQueryParamsEnabled,
    isParticipant,
    isResearcher,
    isTest,
    URLsManager,
} from '$app-web/utils';
import { Cookie } from '$app-web/utils/cookie';

export const SAGA_NAME = 'SPACE_DASHBOARD';

function loadUserDataFromLocalStorage() {
    let userData = null;
    if (window.localStorage) {
        const localStorageDataRaw = localStorage.getItem(process.env.REACT_APP_LOCAL_STORAGE_KEY_AUTH_STORE);
        if (localStorageDataRaw !== null) {
            const localStorageData = JSON.parse(localStorageDataRaw);
            const accessToken = localStorageData?.state?.accessToken;
            if (accessToken != null) {
                // Expose it as `apiKey`, the shape every other source here returns and the one
                // the session check and `seedAuthStore` read.
                userData = {
                    apiKey: accessToken,
                };
            }
        }
    }
    return userData;
}

function loadUserDataFromCookies() {
    const apiKey = Cookie.getItem('access_token');
    if (!apiKey) return null;
    const refreshToken = Cookie.getItem('refresh_token');
    const username = Cookie.getItem('username');
    return {
        apiKey,
        ...(refreshToken && { refreshToken }),
        ...(username && { username }),
    };
}

function loadUserDataFromQueryParams() {
    let userData = null;
    const parsedURL = new URLParse(window.location.href, true);
    if (
        parsedURL.query.username != null &&
        parsedURL.query.username !== '' &&
        parsedURL.query.api_key != null &&
        parsedURL.query.api_key !== ''
    ) {
        userData = {
            username: parsedURL.query.username,
            apiKey: parsedURL.query.api_key,
        };
    }
    return userData;
}

function stripCredentialsFromUrl() {
    // Sender-side handoff was removed in new pdash (redirectToRDash no longer
    // appends username/api_key). This stripper and loadUserDataFromQueryParams
    // are kept temporarily as a fallback for in-flight sessions or stale links.
    // Safe to delete after a soak period confirms nothing hits this path.
    const url = new URL(window.location.href);
    url.searchParams.delete('username');
    url.searchParams.delete('api_key');
    window.history.replaceState({}, '', url.toString());
}

function seedAuthStore(userData) {
    appClientAuth.useAuthenticationStore.getState().updateState({
        accessToken: userData.apiKey,
        ...(userData.refreshToken && { refreshToken: userData.refreshToken }),
        ...(userData.username && { username: userData.username }),
    });
}

function loadUserDataFromEnv() {
    if (isParticipant(process.env.REACT_APP_USER_ROLE)) {
        return {
            username: process.env.REACT_APP_USER_PARTICIPANT_USERNAME,
            apiKey: process.env.REACT_APP_USER_PARTICIPANT_API_KEY,
        };
    } else if (isResearcher(process.env.REACT_APP_USER_ROLE)) {
        return {
            username: process.env.REACT_APP_USER_RESEARCHER_USERNAME,
            apiKey: process.env.REACT_APP_USER_RESEARCHER_API_KEY,
        };
    } else {
        return null;
    }
}

function* initialize() {
    if (URLsManager.space('dashboard').inSpace()) {
        try {
            let userDataFromCookies = null;
            let userDataFromQueryParams = null;
            let userDataFromEnv = null;
            let userDataFromLocalStorage = null;

            userDataFromCookies = yield call(loadUserDataFromCookies);

            if (isLoadUserDataFromQueryParamsEnabled()) {
                userDataFromQueryParams = yield call(loadUserDataFromQueryParams);
                if (userDataFromQueryParams) {
                    yield call(stripCredentialsFromUrl);
                }
            }

            if (isLoadUserDataFromEnvEnabled()) {
                userDataFromEnv = yield call(loadUserDataFromEnv);
            }

            if (isLoadUserDataFromLocalStorageEnabled() || isTest()) {
                userDataFromLocalStorage = yield call(loadUserDataFromLocalStorage);
            }

            const userData = userDataFromEnv || userDataFromCookies || userDataFromQueryParams || userDataFromLocalStorage;

            // No session on the frontend: login now lives in the web-application (new pdash).
            // Redirect there; after a researcher logs in it redirects back to /dashboard/rdash
            // with the shared session cookie, and this bootstrap seeds the auth store below.
            if (!userData || !userData.apiKey) {
                URLsManager.redirectTo(getWebApplicationURL('/dashboard/auth/sign-in?researcher=1'));
                return;
            }

            // Bridge the picked credentials into the Zustand auth store
            // so the API layer sees an accessToken (Bearer auth + refresh flow).
            yield call(seedAuthStore, userData);

            yield put(mainAppStore.userDuck.duckActionCreators.loadUserDataFromSpaceSucceeded(userData, false, true));
            yield take(i18nStore.i18nDuck.duckActions.ASK_SPACE_FOR_LANGUAGE_NAMESPACE);
            const userRole = yield select(mainAppStore.userDuck.duckSelectors.roleSelector);
            yield put(
                i18nStore.i18nDuck.duckActionCreators.askSpaceForLanguageNamespaceSucceeded(
                    userRole === 'researcher' ? 'rdash' : 'pdash'
                )
            );
        } catch (error) {
            errorHandle.anError(error);
            yield put(mainAppStore.userDuck.duckActionCreators.loadUserDataFromSpaceFailed());
        }
    }
}

export default function* dashboardSaga() {
    yield take(mainAppStore.userDuck.duckActions.ASK_SPACE_TO_LOAD_USER_DATA);
    yield call(initialize);
}
